import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, RotateCcw } from "lucide-react";
import { readSSE, aiErrorMessage } from "@/lib/sse";
import { renderRich } from "@/lib/rich.jsx";
import { GoldButton } from "@/components/Section";

import { API } from "@/lib/api";

const inputCls =
  "w-full rounded-2xl bg-[#F8F6F0] border border-[#0E1B31]/10 px-4 py-3.5 text-sm text-[#0E1B31] placeholder:text-[#6B7A8F]/60 focus:outline-none focus:border-[#2C4A6F] focus:bg-white focus:shadow-[0_0_0_4px_rgba(44,74,111,0.08)] transition-all duration-300";

export const InsightsSection = () => {
  const [industry, setIndustry] = useState("");
  const [size, setSize] = useState("");
  const [challenge, setChallenge] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState("");
  const requestRef = useRef(null);
  useEffect(() => () => requestRef.current?.abort(), []);

  const run = async () => {
    if (industry.trim().length < 2 || !size || requestRef.current) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setError("");
    setResult("");
    setStarted(true);
    setLoading(true);
    try {
      await readSSE(
        `${API}/ai/insights`,
        { industry: industry.trim(), size, challenge: challenge.trim() },
        (delta) => setResult((r) => r + delta),
        { signal: controller.signal }
      );
    } catch (e) {
      if (!controller.signal.aborted) setError(aiErrorMessage(e));
    } finally {
      requestRef.current = null;
      if (!controller.signal.aborted) setLoading(false);
    }
  };

  return (
    <div data-testid="insights-section" className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
      <div>
        <p className="inline-flex items-center gap-2 rounded-full bg-[#0E1B31] px-3.5 py-1.5 font-mono-brand text-[10px] tracking-[0.25em] uppercase text-white">
          <Sparkles className="h-3.5 w-3.5 text-[#C5A059]" /> AI Εκτίμηση Ευκαιριών
        </p>
        <h3 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0E1B31] leading-[1.1]">
          Δείτε πού μπορεί να κρύβεται η επόμενη ευκαιρία σας
        </h3>
        <p className="mt-5 text-base leading-relaxed text-[#3B4A5E]">
          Συμπληρώστε δύο πεδία και λάβετε αμέσως μια ενδεικτική, γραπτή εκτίμηση για την
          επιχείρησή σας — με τη στρατηγική ματιά της CRESTIA.
        </p>
        <div className="mt-8 space-y-4">
          <div>
            <label htmlFor="ins-industry" className="block text-xs font-medium text-[#3B4A5E] mb-2">
              Κλάδος δραστηριότητας *
            </label>
            <input
              id="ins-industry"
              data-testid="insights-industry-input"
              className={inputCls}
              value={industry}
              maxLength={120}
              disabled={loading}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="π.χ. παραγωγή τροφίμων, εμπόριο υλικών, logistics"
            />
          </div>
          <div>
            <label htmlFor="ins-size" className="block text-xs font-medium text-[#3B4A5E] mb-2">
              Μέγεθος επιχείρησης *
            </label>
            <select
              id="ins-size"
              data-testid="insights-size-select"
              className={inputCls}
              value={size}
              disabled={loading}
              onChange={(e) => setSize(e.target.value)}
            >
              <option value="">Επιλέξτε...</option>
              <option value="Ατομική ή πολύ μικρή επιχείρηση">Ατομική ή πολύ μικρή</option>
              <option value="Μικρή επιχείρηση (έως 10 άτομα)">Μικρή (έως 10 άτομα)</option>
              <option value="Μικρομεσαία επιχείρηση (10-50 άτομα)">Μικρομεσαία (10–50 άτομα)</option>
              <option value="Μεσαία ή αναπτυσσόμενη επιχείρηση">Μεσαία / αναπτυσσόμενη</option>
            </select>
          </div>
          <div>
            <label htmlFor="ins-challenge" className="block text-xs font-medium text-[#3B4A5E] mb-2">
              Τι σας απασχολεί περισσότερο; <span className="text-[#6B7A8F]">(προαιρετικό)</span>
            </label>
            <input
              id="ins-challenge"
              data-testid="insights-challenge-input"
              className={inputCls}
              value={challenge}
              maxLength={600}
              disabled={loading}
              onChange={(e) => setChallenge(e.target.value)}
              placeholder="π.χ. στασιμότητα πωλήσεων, απορρυθμισμένες διαδικασίες"
            />
          </div>
          <button
            data-testid="insights-submit-button"
            onClick={run}
            disabled={loading || industry.trim().length < 2 || !size}
            className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 bg-[#0E1B31] text-[#F8F6F0] text-sm font-semibold hover:bg-[#2C4A6F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-300 shadow-[0_14px_30px_-14px_rgba(14,27,49,0.6)]"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {loading ? "Σύνταξη εκτίμησης..." : "Δείτε την εκτίμηση"}
          </button>
        </div>
      </div>

      <div
        data-testid="insights-result"
        aria-live="polite"
        aria-busy={loading}
        className="relative overflow-hidden rounded-[32px] mesh-slate border border-[#0E1B31]/[0.07] p-8 lg:p-10 min-h-[420px] flex flex-col shadow-[0_40px_90px_-50px_rgba(14,27,49,0.35)]"
      >
        {!started ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full glass text-[#2C4A6F]"><Sparkles className="h-6 w-6" /></span>
            <p className="mt-5 text-sm text-[#6B7A8F] max-w-xs leading-relaxed">
              Η εκτίμησή σας θα εμφανιστεί εδώ, καθώς συντάσσεται σε πραγματικό χρόνο.
            </p>
          </div>
        ) : (
          <>
            <p className="font-mono-brand text-[11px] tracking-[0.3em] uppercase text-[#2C4A6F]">
              Ενδεικτική Εκτίμηση
            </p>
            <div className="mt-4 rounded-2xl bg-white/80 backdrop-blur p-5 text-sm leading-relaxed text-[#3B4A5E] whitespace-pre-wrap flex-1">
              {renderRich(result)}
              {loading && !result && <p role="status" className="text-sm text-[#6B7A8F]">Εξετάζουμε τον κλάδο, το μέγεθος και την πρόκληση της επιχείρησής σας...</p>}
              {error && (
                <div role="alert" data-testid="insights-error" className="space-y-3">
                  {result && <p className="text-xs">Η παραπάνω εκτίμηση είναι ημιτελής.</p>}
                  <p>{error}</p>
                  <button type="button" data-testid="insights-retry-button" onClick={run}
                    disabled={loading || industry.trim().length < 2 || !size}
                    className="inline-flex min-h-[44px] items-center gap-2 font-semibold text-[#2C4A6F] disabled:opacity-50">
                    <RotateCcw className="h-4 w-4" /> Δοκιμή ξανά
                  </button>
                </div>
              )}
              {loading && <span className="inline-block w-2 h-4 bg-[#C26D52] animate-pulse ml-0.5 align-text-bottom" />}
            </div>
            {!loading && !error && result && (
              <div className="mt-8 pt-6 border-t border-[#0E1B31]/10">
                <p className="text-xs text-[#6B7A8F] leading-relaxed mb-5">
                  Αυτή είναι μια ενδεικτική εκτίμηση με AI. Η πραγματική ανάλυση γίνεται μέσα από
                  τη μεθοδολογία της CRESTIA, με βάση τα δεδομένα της επιχείρησής σας.
                </p>
                <GoldButton to="/contact" testId="insights-cta-button">
                  Ας μιλήσουμε για την επιχείρησή σας
                </GoldButton>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
